import { useEffect } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $isTableNode,
  $isTableRowNode,
  $isTableCellNode,
  $createTableRowNode,
  $createTableCellNode,
  TableCellHeaderStates,
  TableCellNode
} from '@lexical/table';
import { $createParagraphNode, $createTextNode, $getNodeByKey } from 'lexical';
import { $createTableMenuButtonNode, $isTableMenuButtonNode } from './nodes/TableMenuButtonNode';
import { getSentence } from '../translations';

/**
 * Table Interaction Plugin following official Lexical guidelines
 * Uses Node Transforms and Commands instead of DOM manipulation
 */

// Global context menu state
let contextMenu = null;
// Map to track DOM elements to Lexical nodes
const domToNodeMap = new WeakMap();

/**
 * Node transform for table cells to add menu button capability
 * Following the official guide pattern for transforms
 */
function tableCellTransform(node, editor) {
  // Only process table cell nodes
  if (!$isTableCellNode(node)) {
    return;
  }

  // Transform is registered but actual button management happens through DOM events

  // Add menu button if cell is selected (we'll track this differently)
  // For now, we'll add the button logic through DOM events
}

/**
 * Plugin registration function following the official guide pattern
 * A plugin is a function that accepts LexicalEditor and returns cleanup function
 */
export function registerTableInteractionPlugin(editor) {
  console.log('Registering Table Interaction Plugin');
  console.log('Editor element:', editor.getRootElement());

  // Add enhanced table styles
  const addTableStyles = () => {
    if (document.getElementById('table-interaction-styles')) return;

    const style = document.createElement('style');
    style.id = 'table-interaction-styles';
    style.textContent = `
      /* Enhanced table styles following Lexical patterns */
      .editor-input table {
        border-collapse: separate;
        border-spacing: 0;
        margin: 20px 0;
        position: relative;
        background: white;
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        overflow: visible;
      }
      
      .editor-input table td,
      .editor-input table th {
        border: 1px solid #e1e5e9;
        padding: 12px 16px;
        min-width: 100px;
        min-height: 40px;
        position: relative;
        background: white;
        transition: all 0.2s ease;
      }
      
       .editor-input table th {
         background: #cccecf;
         color: #374151;
         font-weight: 600;
         text-align: center;
         border-bottom: 2px solid #e2e8f0;
       }
      
      .editor-input table td:hover {
        background-color: #f8f9ff;
        cursor: text;
        box-shadow: inset 0 0 0 2px #4f46e5;
      }
      
      .editor-input table .selected-cell {
        background-color: #dbeafe !important;
        border-color: #3b82f6 !important;
        box-shadow: inset 0 0 0 2px #3b82f6 !important;
      }
      
      /* Context menu */
      .table-context-menu {
        position: fixed;
        background: white;
        border: 1px solid #d1d5db;
        border-radius: 8px;
        box-shadow: 0 10px 25px rgba(0,0,0,0.15);
        padding: 8px 0;
        min-width: 200px;
        z-index: 1000;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: 14px;
      }
      
      .table-context-menu-item {
        padding: 10px 16px;
        cursor: pointer;
        color: #374151;
        transition: background-color 0.15s ease;
      }
      
      .table-context-menu-item:hover {
        background-color: #f3f4f6;
      }
      
       .table-context-menu-separator {
         height: 1px;
         background-color: #e5e7eb;
         margin: 4px 0;
       }
       
       /* Menu button that appears on cell selection */
       .table-menu-button {
         position: absolute;
         background: #4f46e5;
         color: white;
         border: none;
         border-radius: 50%;
         width: 24px;
         height: 24px;
         font-size: 12px;
         font-weight: bold;
         cursor: pointer;
         display: flex;
         align-items: center;
         justify-content: center;
         z-index: 100;
         box-shadow: 0 2px 8px rgba(0,0,0,0.2);
         transition: all 0.2s ease;
         top: -8px;
         right: -8px;
       }
       
       .table-menu-button:hover {
         background: #3730a3;
         transform: scale(1.1);
         box-shadow: 0 4px 12px rgba(0,0,0,0.3);
       }
       
       
     `;
    document.head.appendChild(style);
  };

  // Add styles immediately
  addTableStyles();

  // Clipboard state
  let copiedCellContent = null;

  // Table operations using Lexical's node API
  const tableOperations = {
    addRowAbove: (cellNode) => {
      editor.update(() => {
        try {
          const tableRow = cellNode.getParent();
          const tableNode = tableRow.getParent();
          
          if (!$isTableRowNode(tableRow) || !$isTableNode(tableNode)) return;

          // Get column count
          const cells = tableRow.getChildren();
          const columnCount = cells.length;

          // Create new row
          const newRow = $createTableRowNode();
          for (let i = 0; i < columnCount; i++) {
            const newCell = $createTableCellNode(TableCellHeaderStates.NO_STATUS);
            const paragraph = $createParagraphNode();
            const textNode = $createTextNode('');
            paragraph.append(textNode);
            newCell.append(paragraph);
            newRow.append(newCell);
          }

          // Insert before current row
          tableRow.insertBefore(newRow);
          console.log('Row added above successfully');
        } catch (error) {
          console.error('Error adding row above:', error);
        }
      });
    },

    addRowBelow: (cellNode) => {
      editor.update(() => {
        try {
          const tableRow = cellNode.getParent();
          const tableNode = tableRow.getParent();
          
          if (!$isTableRowNode(tableRow) || !$isTableNode(tableNode)) return;

          // Get column count
          const cells = tableRow.getChildren();
          const columnCount = cells.length;

          // Create new row
          const newRow = $createTableRowNode();
          for (let i = 0; i < columnCount; i++) {
            const newCell = $createTableCellNode(TableCellHeaderStates.NO_STATUS);
            const paragraph = $createParagraphNode();
            const textNode = $createTextNode('');
            paragraph.append(textNode);
            newCell.append(paragraph);
            newRow.append(newCell);
          }

          // Insert after current row
          tableRow.insertAfter(newRow);
          console.log('Row added below successfully');
        } catch (error) {
          console.error('Error adding row below:', error);
        }
      });
    },

    addColumnLeft: (cellNode) => {
      editor.update(() => {
        try {
          // Get fresh references to avoid stale node issues
          const cellKey = cellNode.getKey();
          const freshCellNode = editor.getEditorState()._nodeMap.get(cellKey);
          
          if (!freshCellNode || !$isTableCellNode(freshCellNode)) {
            console.error('Cell node not found or invalid');
            return;
          }

          const tableRow = freshCellNode.getParent();
          const tableNode = tableRow.getParent();
          
          if (!$isTableRowNode(tableRow) || !$isTableNode(tableNode)) {
            console.error('Invalid table structure');
            return;
          }

          // Get cell index in current row
          const cells = tableRow.getChildren();
          const cellIndex = cells.indexOf(freshCellNode);
          
          if (cellIndex === -1) {
            console.error('Cell not found in row');
            return;
          }

          // Add column to all rows
          const rows = tableNode.getChildren();
          rows.forEach((row, rowIndex) => {
            if ($isTableRowNode(row)) {
              const rowCells = row.getChildren();
              
              // Check if this is a header row (first row or has header cells)
              const isHeaderRow = rowIndex === 0 || rowCells.some(cell => 
                $isTableCellNode(cell) && cell.getHeaderStyles() !== TableCellHeaderStates.NO_STATUS
              );
              
              const newCell = $createTableCellNode(
                isHeaderRow ? TableCellHeaderStates.ROW : TableCellHeaderStates.NO_STATUS
              );
              const paragraph = $createParagraphNode();
              const textNode = $createTextNode('');
              paragraph.append(textNode);
              newCell.append(paragraph);

              // Insert before the target cell
              if (cellIndex < rowCells.length && rowCells[cellIndex]) {
                rowCells[cellIndex].insertBefore(newCell);
              } else {
                // Fallback: append at the beginning
                row.getFirstChild()?.insertBefore(newCell) || row.append(newCell);
              }
            }
          });

          console.log('Column added left successfully');
        } catch (error) {
          console.error('Error adding column left:', error);
        }
      });
    },

    addColumnRight: (cellNode) => {
      editor.update(() => {
        try {
          // Get fresh references to avoid stale node issues
          const cellKey = cellNode.getKey();
          const freshCellNode = editor.getEditorState()._nodeMap.get(cellKey);
          
          if (!freshCellNode || !$isTableCellNode(freshCellNode)) {
            console.error('Cell node not found or invalid');
            return;
          }

          const tableRow = freshCellNode.getParent();
          const tableNode = tableRow.getParent();
          
          if (!$isTableRowNode(tableRow) || !$isTableNode(tableNode)) {
            console.error('Invalid table structure');
            return;
          }

          // Get cell index in current row
          const cells = tableRow.getChildren();
          const cellIndex = cells.indexOf(freshCellNode);
          
          if (cellIndex === -1) {
            console.error('Cell not found in row');
            return;
          }

          // Add column to all rows
          const rows = tableNode.getChildren();
          rows.forEach((row, rowIndex) => {
            if ($isTableRowNode(row)) {
              const rowCells = row.getChildren();
              
              // Check if this is a header row (first row or has header cells)
              const isHeaderRow = rowIndex === 0 || rowCells.some(cell => 
                $isTableCellNode(cell) && cell.getHeaderStyles() !== TableCellHeaderStates.NO_STATUS
              );
              
              const newCell = $createTableCellNode(
                isHeaderRow ? TableCellHeaderStates.ROW : TableCellHeaderStates.NO_STATUS
              );
              const paragraph = $createParagraphNode();
              const textNode = $createTextNode(isHeaderRow ? "" : '');
              paragraph.append(textNode);
              newCell.append(paragraph);

              // Insert after the target cell
              if (cellIndex < rowCells.length && rowCells[cellIndex]) {
                rowCells[cellIndex].insertAfter(newCell);
              } else {
                // Fallback: append at the end
                row.append(newCell);
              }
            }
          });

          console.log('Column added right successfully');
        } catch (error) {
          console.error('Error adding column right:', error);
        }
      });
    },

    deleteRow: (cellNode) => {
      editor.update(() => {
        try {
          // Get fresh references to avoid stale node issues
          const cellKey = cellNode.getKey();
          const freshCellNode = editor.getEditorState()._nodeMap.get(cellKey);
          
          if (!freshCellNode || !$isTableCellNode(freshCellNode)) {
            console.error('Cell node not found or invalid');
            return;
          }

          const tableRow = freshCellNode.getParent();
          const tableNode = tableRow.getParent();
          
          if (!$isTableRowNode(tableRow) || !$isTableNode(tableNode)) {
            console.error('Invalid table structure');
            return;
          }

          // Check if we have more than one row
          const rows = tableNode.getChildren();
          if (rows.length <= 1) {
            alert('Cannot delete the last row');
            return;
          }

          tableRow.remove();
          console.log('Row deleted successfully');
        } catch (error) {
          console.error('Error deleting row:', error);
        }
      });
    },

    deleteColumn: (cellNode) => {
      editor.update(() => {
        try {
          // Get fresh references to avoid stale node issues
          const cellKey = cellNode.getKey();
          const freshCellNode = editor.getEditorState()._nodeMap.get(cellKey);
          
          if (!freshCellNode || !$isTableCellNode(freshCellNode)) {
            console.error('Cell node not found or invalid');
            return;
          }

          const tableRow = freshCellNode.getParent();
          const tableNode = tableRow.getParent();
          
          if (!$isTableRowNode(tableRow) || !$isTableNode(tableNode)) {
            console.error('Invalid table structure');
            return;
          }

          // Get cell index
          const cells = tableRow.getChildren();
          const cellIndex = cells.indexOf(freshCellNode);
          
          if (cellIndex === -1) {
            console.error('Cell not found in row');
            return;
          }

          // Check if we have more than one column
          if (cells.length <= 1) {
            alert('Cannot delete the last column');
            return;
          }

          // Remove column from all rows
          const rows = tableNode.getChildren();
          rows.forEach(row => {
            if ($isTableRowNode(row)) {
              const rowCells = row.getChildren();
              if (cellIndex < rowCells.length && rowCells[cellIndex]) {
                rowCells[cellIndex].remove();
              }
            }
          });

          console.log('Column deleted successfully');
        } catch (error) {
          console.error('Error deleting column:', error);
        }
      });
    },

    clearCell: (cellNode) => {
      editor.update(() => {
        try {
          // Get fresh references to avoid stale node issues
          const cellKey = cellNode.getKey();
          const freshCellNode = editor.getEditorState()._nodeMap.get(cellKey);
          
          if (!freshCellNode || !$isTableCellNode(freshCellNode)) {
            console.error('Cell node not found or invalid');
            return;
          }

          freshCellNode.clear();
          const paragraph = $createParagraphNode();
          const textNode = $createTextNode('');
          paragraph.append(textNode);
          freshCellNode.append(paragraph);
          console.log('Cell cleared successfully');
        } catch (error) {
          console.error('Error clearing cell:', error);
        }
      });
    },

    copyCell: (cellNode) => {
      editor.read(() => {
        try {
          if ($isTableCellNode(cellNode)) {
            const cellContent = cellNode.getTextContent();
            copiedCellContent = cellContent;
            
            // Also copy to system clipboard if available
            if (navigator.clipboard) {
              navigator.clipboard.writeText(cellContent);
            }
            
            console.log('Cell content copied:', cellContent);
          }
        } catch (error) {
          console.error('Error copying cell:', error);
        }
      });
    },

    pasteCell: async (cellNode) => {
      try {
        let contentToPaste = copiedCellContent;
        
        // If no internal content, try to get from system clipboard
        if (contentToPaste === null && navigator.clipboard) {
          try {
            contentToPaste = await navigator.clipboard.readText();
            console.log('Retrieved content from system clipboard:', contentToPaste);
          } catch (clipboardError) {
            console.log('Could not read from system clipboard:', clipboardError);
          }
        }
        
        if (contentToPaste === null || contentToPaste === '') {
          console.log('No content to paste');
          return;
        }

        editor.update(() => {
          try {
            // Get fresh references to avoid stale node issues
            const cellKey = cellNode.getKey();
            const freshCellNode = editor.getEditorState()._nodeMap.get(cellKey);
            
            if (!freshCellNode || !$isTableCellNode(freshCellNode)) {
              console.error('Cell node not found or invalid');
              return;
            }

            freshCellNode.clear();
            const paragraph = $createParagraphNode();
            const textNode = $createTextNode(contentToPaste);
            paragraph.append(textNode);
            freshCellNode.append(paragraph);
            console.log('Cell content pasted:', contentToPaste);
          } catch (error) {
            console.error('Error updating cell with pasted content:', error);
          }
        });
      } catch (error) {
        console.error('Error pasting cell:', error);
      }
    },

  };

  // Track selected cell using Lexical state instead of DOM manipulation
  let selectedCellKey = null;

  // Add menu button to selected cell using Lexical node approach
  const addMenuButtonToCell = (cellNode) => {
    editor.update(() => {
      try {
        // Check if cell already has a menu button
        const children = cellNode.getChildren();
        const hasMenuButton = children.some(child => $isTableMenuButtonNode(child));
        
        if (!hasMenuButton) {
          const menuButtonNode = $createTableMenuButtonNode(
            cellNode.getKey(),
            (event, cellKey) => {
              // Find the cell element for context menu
              const cellElement = document.querySelector(`[data-lexical-key="${cellKey}"]`);
              if (cellElement) {
                // Use the reference element from the event for smart positioning
                const referenceElement = event.referenceElement || cellElement;
                showContextMenu(event, cellElement, cellElement.closest('table'), referenceElement);
              }
            }
          );
          
          // Add the menu button as the last child of the cell
          cellNode.append(menuButtonNode);
          console.log('Menu button node added to cell');
        }
      } catch (error) {
        console.error('Error adding menu button to cell:', error);
      }
    });
  };

  const removeMenuButtonFromCell = (cellNode) => {
    editor.update(() => {
      try {
        const children = cellNode.getChildren();
        const menuButtonNode = children.find(child => $isTableMenuButtonNode(child));
        
        if (menuButtonNode) {
          menuButtonNode.remove();
          console.log('Menu button node removed from cell');
        }
      } catch (error) {
        console.error('Error removing menu button from cell:', error);
      }
    });
  };

  const updateSelectedCell = (newCellKey) => {
    // Remove button from previously selected cell
    if (selectedCellKey && (newCellKey !== selectedCellKey) && (newCellKey !== null)) {
      editor.read(() => {
        const prevCellNode = $getNodeByKey(selectedCellKey);
        if (prevCellNode && $isTableCellNode(prevCellNode)) {
          removeMenuButtonFromCell(prevCellNode);
        }
      });
    }

    // Add button to newly selected cell
    selectedCellKey = newCellKey;

    if (newCellKey && (newCellKey !== null)) {
      editor.read(() => {
        const cellNode = $getNodeByKey(newCellKey);
        if (cellNode && (cellNode != null) && $isTableCellNode(cellNode)) {
          addMenuButtonToCell(cellNode);
        }
      });
    }
  };

  // Position menu within screen bounds with smart positioning
  const getMenuPosition = (x, y, referenceElement = null) => {
    const menuWidth = 200; // Approximate menu width
    const menuHeight = 280; // Approximate menu height (8 items * 35px each)
    const padding = 15; // Padding from screen edges
    const arrowOffset = 10; // Space for potential arrow indicators

    let adjustedX = x;
    let adjustedY = y;

    // Get viewport dimensions
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
    const scrollY = window.pageYOffset || document.documentElement.scrollTop;

    // Note: Scroll position adjustment available if needed
    // const absoluteX = x + scrollX;
    // const absoluteY = y + scrollY;

    // Check right boundary - if menu would overflow, position to the left
    if (x + menuWidth > viewportWidth - padding) {
      if (referenceElement) {
        // Position to the left of the reference element
        const rect = referenceElement.getBoundingClientRect();
        adjustedX = rect.left - menuWidth - arrowOffset;
      } else {
        // Standard left adjustment
        adjustedX = viewportWidth - menuWidth - padding;
      }
      
      // Ensure we don't go too far left
      if (adjustedX < padding) {
        adjustedX = padding;
      }
    }

    // Check left boundary
    if (adjustedX < padding) {
      adjustedX = padding;
    }

    // Check bottom boundary - if menu would overflow, position above
    if (y + menuHeight > viewportHeight - padding) {
      if (referenceElement) {
        // Position above the reference element
        const rect = referenceElement.getBoundingClientRect();
        adjustedY = rect.top - menuHeight - arrowOffset;
      } else {
        // Standard top adjustment
        adjustedY = viewportHeight - menuHeight - padding;
      }
      
      // Ensure we don't go too far up
      if (adjustedY < padding) {
        adjustedY = padding;
      }
    }

    // Check top boundary
    if (adjustedY < padding) {
      adjustedY = padding;
    }

    // Final boundary checks to ensure menu is always fully visible
    adjustedX = Math.max(padding, Math.min(adjustedX, viewportWidth - menuWidth - padding));
    adjustedY = Math.max(padding, Math.min(adjustedY, viewportHeight - menuHeight - padding));

    console.log(`Menu positioning: original(${x}, ${y}) → adjusted(${adjustedX}, ${adjustedY})`);
    console.log(`Viewport: ${viewportWidth}x${viewportHeight}, Menu: ${menuWidth}x${menuHeight}`);

    return { x: adjustedX, y: adjustedY };
  };

  // DOM interaction handlers (minimal DOM manipulation)
  const handleTableInteraction = (e) => {
    console.log('handleTableInteraction called, event type:', e.type, 'target:', e.target);
    
    const cell = e.target.closest('td, th');
    const table = e.target.closest('table');
    
    console.log('Found cell:', cell, 'Found table:', table);
    
    if (!cell || !table) {
      console.log('No cell or table found, returning');
      return;
    }

    switch (e.type) {
      case 'click':
        console.log('Table cell clicked:', cell);
        
        // Don't handle clicks on the menu button itself
        if (e.target.classList.contains('table-menu-button')) {
          console.log('Clicked on menu button, ignoring');
          return;
        }
        
        // Visual selection feedback
        document.querySelectorAll('.selected-cell').forEach(el => {
          el.classList.remove('selected-cell');
        });
        cell.classList.add('selected-cell');
        
        // Get the Lexical node key and update selected cell
        const nodeKey = cell.getAttribute('data-lexical-key');
        if (nodeKey) {
          console.log('Updating selected cell with key:', nodeKey);
          updateSelectedCell(nodeKey);
        } else {
          console.log('No lexical key found for cell, trying to establish mapping');
          // Try to establish mapping
          editor.read(() => {
            const root = editor.getEditorState()._nodeMap;
            const cellText = cell.textContent;
            
            root.forEach((node, key) => {
              if ($isTableCellNode(node)) {
                const nodeText = node.getTextContent();
                if (nodeText === cellText && !cell.hasAttribute('data-lexical-key')) {
                  cell.setAttribute('data-lexical-key', key);
                  domToNodeMap.set(cell, node);
                  updateSelectedCell(key);
                }
              }
            });
          });
        }
        break;

      case 'contextmenu':
        console.log('Table cell right-clicked');
        e.preventDefault();
        showContextMenu(e, cell, table);
        break;
        
      default:
        console.log('Unhandled event type:', e.type);
    }
  };

  const enableCellEditing = (cellElement) => {
    const currentText = cellElement.textContent;
    const input = document.createElement('textarea');
    
    input.value = currentText;
    input.style.cssText = `
      width: calc(100% - 30px);
      height: 100%;
      border: none;
      outline: 2px solid #3b82f6;
      padding: 8px;
      font-size: inherit;
      font-family: inherit;
      background: white;
      resize: none;
      border-radius: 4px;
      min-height: 40px;
      margin-right: 30px;
    `;

    const saveEdit = () => {
      const newContent = input.value;
      const cellElement = input.parentElement;
      input.remove();
      
      // Update through Lexical node API using reliable mapping
      let targetCellNode = null;
      
      // Get the node from our reliable mapping
      if (domToNodeMap.has(cellElement)) {
        targetCellNode = domToNodeMap.get(cellElement);
      } else {
        // Fallback: use data attribute
        const nodeKey = cellElement.getAttribute('data-lexical-key');
        if (nodeKey) {
          editor.read(() => {
            const root = editor.getEditorState()._nodeMap;
            const node = root.get(nodeKey);
            if (node && $isTableCellNode(node)) {
              targetCellNode = node;
              domToNodeMap.set(cellElement, node); // Update mapping
            }
          });
        }
      }
      
      // Update the cell content
      if (targetCellNode) {
        editor.update(() => {
          targetCellNode.clear();
          const paragraph = $createParagraphNode();
          const textNode = $createTextNode(newContent);
          paragraph.append(textNode);
          targetCellNode.append(paragraph);
          console.log('Cell content updated via Lexical node API');
        });
      }
      
        // Restore the menu button after editing
        setTimeout(() => {
          if (cellElement && cellElement.classList.contains('selected-cell')) {
            const nodeKey = cellElement.getAttribute('data-lexical-key');
            if (nodeKey) {
              updateSelectedCell(nodeKey);
            }
          }
        }, 100);
    };

    input.addEventListener('blur', saveEdit);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        saveEdit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        input.remove();
        // Restore menu button on escape
        if (cellElement && cellElement.classList.contains('selected-cell')) {
          const nodeKey = cellElement.getAttribute('data-lexical-key');
          if (nodeKey) {
            updateSelectedCell(nodeKey);
          }
        }
      }
    });

    // Preserve the menu button while editing
    const existingButton = cellElement.querySelector('.table-menu-button');
    cellElement.innerHTML = '';
    cellElement.appendChild(input);
    if (existingButton) {
      cellElement.appendChild(existingButton);
    }
    input.focus();
    input.select();
  };

  const showContextMenu = (e, cellElement, tableElement, referenceElement = null) => {
    // Remove existing menu
    if (contextMenu) {
      contextMenu.remove();
      contextMenu = null;
    }

    contextMenu = document.createElement('div');
    contextMenu.className = 'table-context-menu';
    
    // Position menu within screen bounds, using reference element for smart positioning
    const reference = referenceElement || cellElement;
    const position = getMenuPosition(e.clientX, e.clientY, reference);
    contextMenu.style.left = position.x + 'px';
    contextMenu.style.top = position.y + 'px';

    // Find the corresponding Lexical nodes using reliable mapping
    const findTableNodes = () => {
      let tableNode = null;
      let cellNode = null;
      
      // First try to get the node from our mapping
      if (domToNodeMap.has(cellElement)) {
        cellNode = domToNodeMap.get(cellElement);
      } else {
        // Fallback: try to find by data attribute
        const nodeKey = cellElement.getAttribute('data-lexical-key');
        if (nodeKey) {
          editor.read(() => {
            const root = editor.getEditorState()._nodeMap;
            const node = root.get(nodeKey);
            if (node && $isTableCellNode(node)) {
              cellNode = node;
              domToNodeMap.set(cellElement, node); // Update mapping
            }
          });
        }
      }
      
      // Find the table node if we have a cell node
      if (cellNode) {
        editor.read(() => {
          const tableRow = cellNode.getParent();
          if ($isTableRowNode(tableRow)) {
            const table = tableRow.getParent();
            if ($isTableNode(table)) {
              tableNode = table;
            }
          }
        });
      }
      
      return { tableNode, cellNode };
    };

    const menuItems = [
      { 
        text: getSentence(177), 
        action: () => {
          console.log('Copy cell menu clicked');
          const { cellNode } = findTableNodes();
          if (cellNode) {
            tableOperations.copyCell(cellNode);
          }
        }
      },
      { 
        text: getSentence(178), 
        action: async () => {
          console.log('Paste cell menu clicked');
          const { cellNode } = findTableNodes();
          if (cellNode) {
            await tableOperations.pasteCell(cellNode);
          }
        }
      },
      { 
        text: getSentence(179), 
        action: () => {
          console.log('Clear cell menu clicked');
          const { cellNode } = findTableNodes();
          if (cellNode) {
            tableOperations.clearCell(cellNode);
          }
        }
      },
      { separator: true },
      { 
        text: getSentence(180), 
        action: () => {
          console.log('Add row above menu clicked');
          const { cellNode } = findTableNodes();
          if (cellNode) {
            tableOperations.addRowAbove(cellNode);
          }
        }
      },
      { 
        text: getSentence(181), 
        action: () => {
          console.log('Add row below menu clicked');
          const { cellNode } = findTableNodes();
          if (cellNode) {
            tableOperations.addRowBelow(cellNode);
          }
        }
      },
      { 
        text: getSentence(182), 
        action: () => {
          console.log('Add column left menu clicked');
          const { cellNode } = findTableNodes();
          if (cellNode) {
            tableOperations.addColumnLeft(cellNode);
          }
        }
      },
      { 
        text: getSentence(183), 
        action: () => {
          console.log('Add column right menu clicked');
          const { cellNode } = findTableNodes();
          if (cellNode) {
            tableOperations.addColumnRight(cellNode);
          }
        }
      },
      { separator: true },
      { 
        text: getSentence(184), 
        action: () => {
          console.log('Delete row menu clicked');
          const { cellNode } = findTableNodes();
          if (cellNode && window.confirm('Delete this row?')) {
            tableOperations.deleteRow(cellNode);
          }
        }
      },
      { 
        text: getSentence(185), 
        action: () => {
          console.log('Delete column menu clicked');
          const { cellNode } = findTableNodes();
          if (cellNode && window.confirm('Delete this column?')) {
            tableOperations.deleteColumn(cellNode);
          }
        }
      },
    ];

    menuItems.forEach(item => {
      if (item.separator) {
        const separator = document.createElement('div');
        separator.className = 'table-context-menu-separator';
        contextMenu.appendChild(separator);
      } else {
        const menuItem = document.createElement('div');
        menuItem.className = 'table-context-menu-item';
        menuItem.textContent = item.text;
        menuItem.onclick = async (e) => {
          e.stopPropagation();
          try {
            console.log('Executing menu action:', item.text);
            await item.action();
            if (contextMenu) {
              contextMenu.remove();
              contextMenu = null;
            }
          } catch (error) {
            console.error('Error executing menu action:', error);
            if (contextMenu) {
              contextMenu.remove();
              contextMenu = null;
            }
          }
        };
        contextMenu.appendChild(menuItem);
      }
    });

    document.body.appendChild(contextMenu);

    // Remove menu on outside click
    setTimeout(() => {
      const removeMenu = (e) => {
        if (contextMenu && !contextMenu.contains(e.target)) {
          contextMenu.remove();
          contextMenu = null;
          document.removeEventListener('click', removeMenu);
        }
      };
      document.addEventListener('click', removeMenu);
    }, 100);
  };

  // Add DOM event listeners (minimal DOM interaction)
  const editorElement = editor.getRootElement();
  console.log('Setting up event listeners on editor element:', editorElement);
  
  if (editorElement) {
    console.log('Adding event listeners to editor element');
    editorElement.addEventListener('click', handleTableInteraction);
    editorElement.addEventListener('contextmenu', handleTableInteraction);
    
    // Test if event listeners are working
    console.log('Event listeners added successfully');
    
    // Test: Add a visible button to the first table cell for debugging
    setTimeout(() => {
      const firstCell = editorElement.querySelector('table td, table th');
      if (firstCell) {
        console.log('Found first table cell for testing:', firstCell);
        firstCell.style.position = 'relative';
        
        // Create test button
        const testButton = document.createElement('button');
        testButton.innerHTML = '⌄';
        testButton.style.cssText = `
          position: absolute;
          background: red;
          color: white;
          border: none;
          border-radius: 50%;
          width: 24px;
          height: 24px;
          top: -8px;
          right: -8px;
          z-index: 1000;
          cursor: pointer;
        `;
        testButton.onclick = () => console.log('Test button clicked!');
        
        firstCell.appendChild(testButton);
        console.log('Test button added to first cell');
      } else {
        console.log('No table cells found for testing');
      }
    }, 1000);
    
    // Hide menu button when clicking outside tables
    document.addEventListener('click', (e) => {
      const cell = e.target.closest('td, th');
      const table = e.target.closest('table');
      const menuButton = e.target.closest('.table-menu-button');
      
      // Hide button if clicking outside table (but not on menu button)
      if (!cell && !table && !menuButton && !e.target.closest('.table-context-menu')) {
        updateSelectedCell(null); // Clear selection
      }
    });
    
    // Make table cells focusable and establish node mappings
    const observer = new MutationObserver(() => {
      const tables = editorElement.querySelectorAll('table');
      tables.forEach(table => {
        const cells = table.querySelectorAll('td, th');
        cells.forEach((cell) => {
          cell.setAttribute('tabindex', '0');
          
          // Establish mapping between DOM cell and Lexical node if not already done
          if (!cell.hasAttribute('data-lexical-key') && !domToNodeMap.has(cell)) {
            editor.read(() => {
              const root = editor.getEditorState()._nodeMap;
              const cellText = cell.textContent;
              
              // Find the corresponding Lexical node
              root.forEach((node, nodeKey) => {
                if ($isTableCellNode(node)) {
                  const lexicalText = node.getTextContent();
                  
                  // More sophisticated matching: consider position and content
                  if (lexicalText === cellText && !cell.hasAttribute('data-lexical-key')) {
                    // Check if this cell already has a mapping to avoid duplicates
                    let alreadyMapped = false;
                    document.querySelectorAll(`[data-lexical-key="${nodeKey}"]`).forEach(mappedCell => {
                      if (mappedCell !== cell) {
                        alreadyMapped = true;
                      }
                    });
                    
                    if (!alreadyMapped) {
                      cell.setAttribute('data-lexical-key', nodeKey);
                      domToNodeMap.set(cell, node);
                    }
                  }
                }
              });
            });
          }
        });
      });
    });
    
    observer.observe(editorElement, { childList: true, subtree: true });

    // Register the table cell transform - use the actual node class, not the check function
    const unregisterTransform = editor.registerNodeTransform(
      TableCellNode, 
      (node) => tableCellTransform(node, editor)
    );

    // Return cleanup function
    return () => {
      editorElement.removeEventListener('click', handleTableInteraction);
      editorElement.removeEventListener('contextmenu', handleTableInteraction);
      observer.disconnect();
      unregisterTransform();
      
      // Clean up selection and context menu
      updateSelectedCell(null);
      if (contextMenu) {
        contextMenu.remove();
        contextMenu = null;
      }
    };
  }

  return () => {}; // Fallback cleanup
}

/**
 * React wrapper component for the plugin
 * This makes it easy to use in React applications
 */
export default function TableInteractionPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return registerTableInteractionPlugin(editor);
  }, [editor]);

  return null;
}
